import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, ApiError } from "@/lib/api";
import { Eye, EyeOff, Check } from "lucide-react";

const PERKS = [
  "Free for up to 1,000 subscribers",
  "AI campaign builder included",
  "No credit card required",
  "Cancel any time",
];

export default function RegisterPage() {
  const navigate    = useNavigate();
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [company, setCompany]   = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setError(""); setLoading(true);
    try {
      const res = await auth.register({ name, email, password, company: company || undefined });
      if (res.token) localStorage.setItem("moremail_token", res.token);
      if ((res as any).requiresVerification) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign up failed");
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-paper">
      {/* Left */}
      <div className="hidden lg:flex lg:w-[40%] bg-ink flex-col justify-center p-12 xl:p-16 gap-10">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-coral rounded-xl flex items-center justify-center text-white font-display font-bold text-base -rotate-2 shadow-lg shadow-coral/40">M</div>
          <span className="font-display font-bold text-2xl text-white tracking-tight">MoreMail</span>
        </Link>
        <div>
          <h2 className="font-display text-3xl font-bold text-white mb-6 leading-tight">Everything you need to<br />send emails that convert.</h2>
          <div className="space-y-3">
            {PERKS.map((p) => (
              <div key={p} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-teal flex items-center justify-center flex-shrink-0">
                  <Check size={11} className="text-white" />
                </div>
                <span className="text-white/70 text-sm">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8">
        <Link to="/" className="flex items-center gap-2 mb-10 lg:hidden">
          <div className="w-9 h-9 bg-coral rounded-xl flex items-center justify-center text-white font-display font-bold text-base -rotate-2 shadow-md shadow-coral/30">M</div>
          <span className="font-display font-bold text-2xl tracking-tight">MoreMail</span>
        </Link>

        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-bold text-ink mb-1.5">Create your account</h1>
          <p className="text-sm text-slate mb-8">Free to start, no credit card needed.</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-5">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Full name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all"
                  placeholder="Jane Smith" autoComplete="name" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Company <span className="text-slate/50 normal-case font-normal">(optional)</span></label>
                <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all"
                  placeholder="Acme Inc." autoComplete="organization" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Work email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all"
                placeholder="you@company.com" autoComplete="email" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all pr-10"
                  placeholder="8+ characters" autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {password && password.length < 8 && (
                <p className="text-xs text-red-500 mt-1.5">At least 8 characters required</p>
              )}
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral-dark disabled:opacity-60 transition-colors shadow-md shadow-coral/25">
              {loading ? "Creating account…" : "Create free account"}
            </button>
          </form>

          <p className="text-center text-xs text-slate mt-5 leading-relaxed">
            By creating an account you agree to our{" "}
            <a href="#" className="text-ink hover:underline">Terms of Service</a>
            {" "}and{" "}
            <a href="#" className="text-ink hover:underline">Privacy Policy</a>.
          </p>

          <p className="text-center text-sm text-slate mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-coral font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
